import type { AGENT_ERROR_CODES } from "@amtana/agent-contract";
import type { AGENT_RUNTIME_ERROR_CODES, BROWSER_ERROR_CODES } from "@amtana/agent-runtime-contract";
import type { ErrorCopyFor } from "@amtana/browser";
import { phrase } from "@amtana/i18n/catalog";
import type { Catalogs, Translation } from "@amtana/i18n/catalog";

// What a member reads when an agent door or a task door refuses (packages/module ADR-001), in
// English and Spanish. The task doors are the agent runtime's; the agent's page is where they show.

const EN = {
  notFoundTitle: "This agent doesn't exist",
  notFoundDescription: "It may have been deleted. Open Agents to see the ones you have.",
  nameTakenTitle: "You already have an agent with this name",
  nameTakenDescription: "Pick another name, so you can tell them apart.",
  modelKeyMissingTitle: "Our model is not available yet",
  modelKeyMissingDescription: "Run the agent on your own Claude or Codex for now.",
  poolFullTitle: "The pool is busy",
  poolFullDescription: "Every machine we run is awake right now. Give the task again in a minute.",
  taskNotFoundTitle: "This task doesn't exist",
  taskNotFoundDescription: "It may belong to an agent that was deleted. Open the agent to see its tasks.",
  taskEndedTitle: "This task already ended",
  taskEndedDescription: "There is nothing left to stop. Its steps stay on the agent's page.",
  scheduleInvalidTitle: "That schedule never comes",
  scheduleInvalidDescription: "Pick a day and a time that exist, such as every Monday at 09:00.",
  screenNotLiveTitle: "The screen is not live",
  screenNotLiveDescription: "Watch the agent first, so its machine wakes, then take the screen.",
} as const;

const ES: Translation<typeof EN> = {
  notFoundTitle: "Este agente no existe",
  notFoundDescription: "Puede que se haya eliminado. Abre Agentes para ver los que tienes.",
  nameTakenTitle: "Ya tienes un agente con este nombre",
  nameTakenDescription: "Elige otro nombre para distinguirlos.",
  modelKeyMissingTitle: "Nuestro modelo aún no está disponible",
  modelKeyMissingDescription: "Por ahora, usa el agente con tu propio Claude o Codex.",
  poolFullTitle: "El pool está ocupado",
  poolFullDescription: "Todas nuestras máquinas están despiertas ahora. Da la tarea de nuevo en un minuto.",
  taskNotFoundTitle: "Esta tarea no existe",
  taskNotFoundDescription: "Puede ser de un agente que se eliminó. Abre el agente para ver sus tareas.",
  taskEndedTitle: "Esta tarea ya terminó",
  taskEndedDescription: "No queda nada que detener. Sus pasos siguen en la página del agente.",
  scheduleInvalidTitle: "Ese horario nunca llega",
  scheduleInvalidDescription: "Elige un día y una hora que existan, como cada lunes a las 09:00.",
  screenNotLiveTitle: "La pantalla no está en vivo",
  screenNotLiveDescription: "Mira primero al agente para que su máquina despierte y luego toma la pantalla.",
};

export const AGENT_ERROR_CATALOGS: Catalogs<typeof EN> = { en: EN, es: ES };

export const AGENT_ERROR_COPY = {
  agent_not_found: {
    title: phrase(AGENT_ERROR_CATALOGS, "notFoundTitle"),
    description: phrase(AGENT_ERROR_CATALOGS, "notFoundDescription"),
  },
  agent_name_taken: {
    title: phrase(AGENT_ERROR_CATALOGS, "nameTakenTitle"),
    description: phrase(AGENT_ERROR_CATALOGS, "nameTakenDescription"),
  },
  model_key_missing: {
    title: phrase(AGENT_ERROR_CATALOGS, "modelKeyMissingTitle"),
    description: phrase(AGENT_ERROR_CATALOGS, "modelKeyMissingDescription"),
  },
} satisfies ErrorCopyFor<typeof AGENT_ERROR_CODES>;

export const AGENT_RUNTIME_ERROR_COPY = {
  computer_pool_full: {
    title: phrase(AGENT_ERROR_CATALOGS, "poolFullTitle"),
    description: phrase(AGENT_ERROR_CATALOGS, "poolFullDescription"),
  },
  task_not_found: {
    title: phrase(AGENT_ERROR_CATALOGS, "taskNotFoundTitle"),
    description: phrase(AGENT_ERROR_CATALOGS, "taskNotFoundDescription"),
  },
  task_ended: {
    title: phrase(AGENT_ERROR_CATALOGS, "taskEndedTitle"),
    description: phrase(AGENT_ERROR_CATALOGS, "taskEndedDescription"),
  },
  schedule_invalid: {
    title: phrase(AGENT_ERROR_CATALOGS, "scheduleInvalidTitle"),
    description: phrase(AGENT_ERROR_CATALOGS, "scheduleInvalidDescription"),
  },
} satisfies ErrorCopyFor<typeof AGENT_RUNTIME_ERROR_CODES>;

export const BROWSER_ERROR_COPY = {
  screen_not_live: {
    title: phrase(AGENT_ERROR_CATALOGS, "screenNotLiveTitle"),
    description: phrase(AGENT_ERROR_CATALOGS, "screenNotLiveDescription"),
  },
} satisfies ErrorCopyFor<typeof BROWSER_ERROR_CODES>;
